import { useCallback, useEffect, useRef, useState } from 'react';
export function useTimer(options) {
    const { initialTime, onComplete, onTick, autoStart = false, interval = 1000 } = options;
    const [timeRemaining, setTimeRemaining] = useState(initialTime);
    const [isRunning, setIsRunning] = useState(autoStart);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);
    const onCompleteRef = useRef(onComplete);
    const onTickRef = useRef(onTick);
    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);
    useEffect(() => {
        onTickRef.current = onTick;
    }, [onTick]);
    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (isRunning && !isCompleted) {
            intervalRef.current = setInterval(() => {
                setTimeRemaining(prevTime => {
                    const newTime = Math.max(0, prevTime - 1);
                    if (onTickRef.current) {
                        onTickRef.current(newTime);
                    }
                    if (newTime === 0) {
                        setIsRunning(false);
                        setIsCompleted(true);
                        setIsPaused(false);
                        if (onCompleteRef.current) {
                            onCompleteRef.current();
                        }
                    }
                    return newTime;
                });
            }, interval);
        }
        else if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning, isCompleted, interval]);
    const start = useCallback(() => {
        if (!isCompleted && timeRemaining > 0) {
            setIsRunning(true);
            setIsPaused(false);
        }
    }, [isCompleted, timeRemaining]);
    const pause = useCallback(() => {
        setIsRunning(false);
        setIsPaused(true);
    }, []);
    const reset = useCallback(() => {
        setIsRunning(false);
        setIsPaused(false);
        setIsCompleted(false);
        setTimeRemaining(initialTime);
    }, [initialTime]);
    const restart = useCallback(() => {
        setIsRunning(false);
        setIsPaused(false);
        setIsCompleted(false);
        setTimeRemaining(initialTime);
        setTimeout(() => setIsRunning(true), 0);
    }, [initialTime]);
    const setTime = useCallback((newTime) => {
        const validTime = Math.max(0, newTime);
        setTimeRemaining(validTime);
        setIsCompleted(validTime === 0);
        if (validTime > 0 && isCompleted) {
            setIsCompleted(false);
        }
    }, [isCompleted]);
    const addTime = useCallback((additionalTime) => {
        setTimeRemaining(prevTime => {
            const newTime = Math.max(0, prevTime + additionalTime);
            if (newTime > 0 && isCompleted) {
                setIsCompleted(false);
            }
            return newTime;
        });
    }, [isCompleted]);
    const formattedTime = useCallback(() => {
        const minutes = Math.floor(timeRemaining / 60);
        const seconds = timeRemaining % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, [timeRemaining]);
    const progress = useCallback(() => {
        if (initialTime === 0)
            return 100;
        return Math.round(((initialTime - timeRemaining) / initialTime) * 100);
    }, [initialTime, timeRemaining]);
    return {
        timeRemaining,
        isRunning,
        isCompleted,
        isPaused,
        start,
        pause,
        reset,
        restart,
        setTime,
        addTime,
        formattedTime: formattedTime(),
        progress: progress(),
    };
}
export default useTimer;
